import React, { useEffect, useMemo, useReducer } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import TextInput from "../../components/Common/TextInput";
import { useAccounts } from "../../DataStore/AccountsContext";
import { useTag } from "../../DataStore/TagContext";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../../components/Common/Notification";
import { useRefreshTrans } from "../../DataStore/useRefreshTrans";
import VendorSelector from "../../components/Transaction/VendorSelector";
import ajaxAsset from "../../util/remote/ajaxAsset";
import SectionTitle from "../../components/Common/SectionTitle";
import AccountingHint from "../../components/Ledger/AccountingHint";

const initialState = {
  loading: false,

  expenseAccount: null, // option object (depreciation expense)
  assetAccount: null,   // option object
  amount: "",
  description: "",
  transactionRef: "",
  transactionDate: "",

  payee: null, // valuer option object
  tag: null,   // option object
};

const reducer = (state, action) => {
  switch (action.type) {
    case "RESET_PARTIAL":
      return { ...state, amount: "", description: "" };
    default:
      return { ...state, [action.type]: action.value };
  }
};

// 🔹 helper: consistent responsive columns (1 / 2 / 3 per row)
const Col = ({ children }) => <div className="col-12 col-md-6 col-xl-4">{children}</div>;

const DepreciateAssetPage = () => {
  const { openSettings, setNotify, currentNotify } = useTheme();
  const refreshBalances = useRefreshTrans();
  const [state, dispatch] = useReducer(reducer, initialState);

  const { expenseAccountListing, assetAccountListing, getAssetAccounts, getExpenseAccounts } =
    useAccounts();

  const { getTagListing, tagListing } = useTag();

  useEffect(() => {
    getExpenseAccounts();
    getAssetAccounts();
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assetOptions = useMemo(() => {
    const opts = [];
    if (Array.isArray(assetAccountListing)) {
      assetAccountListing.forEach((a) =>
        opts.push({ label: `${a.account_number} ${a.account_name}`, value: a.account_id })
      );
    }
    return opts;
  }, [assetAccountListing]);

  const expenseOptions = useMemo(() => {
    const opts = [];
    if (Array.isArray(expenseAccountListing)) {
      expenseAccountListing.forEach((a) =>
        opts.push({ label: `${a.account_number} ${a.account_name}`, value: a.account_id })
      );
    }
    return opts;
  }, [expenseAccountListing]);

  const tagOptions = useMemo(() => {
    const opts = [
      { label: "Create New Project...", value: "add" },
      { label: "- No project -", value: "0" },
    ];
    if (Array.isArray(tagListing)) {
      tagListing.forEach((t) => opts.push({ label: t.tag_name, value: t.tag_id }));
    }
    return opts;
  }, [tagListing]);

  const hint = useMemo(() => {
    const asset = state.assetAccount?.label || "Asset Account";
    const dep = state.expenseAccount?.label || "Depreciation Expense";
    const valuer = state.payee?.label || "Asset Valuer";
    return `Depreciation journal: Debit ${dep}, Credit ${asset} (Assessed by ${valuer}). This reduces the carrying value of the asset.`;
  }, [state.assetAccount, state.expenseAccount, state.payee]);

  const onConfirm = async (event) => {
    event.preventDefault();

    if (Number(state.amount) <= 0) {
      setNotify(<Notification type="danger" message="Enter amount and try again." />);
      return;
    }

    if (!state.expenseAccount?.value || !state.assetAccount?.value) {
      setNotify(<Notification type="danger" message="Select both expense category and asset account." />);
      return;
    }

    if (!state.payee?.value) {
      setNotify(
        <Notification type="danger" message="Select valuer - i.e. the company/person who valued the asset." />
      );
      return;
    }

    dispatch({ type: "loading", value: true });

    const server_response = await ajaxAsset.depreciateValue(
      state.assetAccount.value,
      state.expenseAccount.value,
      state.amount,
      state.transactionDate,
      state.description,
      state.payee.value,
      state.transactionRef,
      state.tag?.value || "0"
    );

    dispatch({ type: "loading", value: false });

    if (server_response.status === "OK") {
      dispatch({ type: "RESET_PARTIAL" });
      setNotify(<Notification type="success" message={server_response.message} />);
      refreshBalances();
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Depreciate Asset"
          description="Record a loss in asset value to keep track of its current value."
          iconClass="fa fa-arrow-down"
        />

        <div className="mb-3">
          <AccountingHint hint={hint} />
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            {currentNotify}

            <form method="post" onSubmit={onConfirm}>
              {/* ---------- ACCOUNTS ---------- */}
              <div className="row mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-university"
                    title="Depreciation Accounts"
                    note="Choose the asset account being reduced and the expense account tracking the loss."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Asset Account"
                    placeholder="Select asset account..."
                    leftIcon="fa fa-university"
                    options={assetOptions}
                    onChange={(item) => dispatch({ type: "assetAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getAssetAccounts(true)} />}
                    loading={!assetAccountListing}
                    value={state.assetAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="select"
                    label="Expense Category"
                    placeholder="Select depreciation expense account..."
                    leftIcon="fa fa-university"
                    options={expenseOptions}
                    onChange={(item) => dispatch({ type: "expenseAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getExpenseAccounts(true)} />}
                    loading={!expenseAccountListing}
                    value={state.expenseAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="number"
                    label="Depreciation Amount"
                    placeholder="By how much has the asset depreciated?"
                    leftIcon="fa fa-credit-card"
                    value={state.amount}
                    onChange={(e) => dispatch({ type: "amount", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>
              </div>

              {/* ---------- DETAILS ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-info-circle"
                    title="Depreciation Details"
                    note="Provide valuation details and references for audit trail."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="date"
                    label="Transaction Date"
                    placeholder="Transaction Date"
                    leftIcon="fa fa-calendar-alt"
                    value={state.transactionDate}
                    onChange={(e) => dispatch({ type: "transactionDate", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="text"
                    label="Transaction Ref"
                    placeholder="Transaction Ref"
                    leftIcon="fa fa-hashtag"
                    value={state.transactionRef}
                    onChange={(e) => dispatch({ type: "transactionRef", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <VendorSelector
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Vendor", "NewVendor");
                        return;
                      }
                      dispatch({ type: "payee", value: item });
                    }}
                    placeholder="Select asset valuer"
                    label="Asset Valuer"
                    value={state.payee}
                    wrap
                  />
                </Col>
              </div>

              {/* Description full width */}
              <div className="row" style={{ rowGap: 12 }}>
                <div className="col-12">
                  <TextInput
                    type="textarea"
                    label="Depreciation Description"
                    placeholder="Describe the details of this depreciation"
                    leftIcon="fa fa-list"
                    value={state.description}
                    onChange={(e) => dispatch({ type: "description", value: e.target.value })}
                    required
                    wrap
                  />
                </div>
              </div>

              {/* ---------- CLASSIFICATION ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-tag"
                    title="Classification"
                    note="Optional: attach a project to improve reporting."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Tag Transaction"
                    placeholder={state.tag?.label || "Transaction is not tagged..."}
                    leftIcon="fa fa-tag"
                    options={tagOptions}
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Project", "NewTag", null, "Create Project");
                        return;
                      }
                      dispatch({ type: "tag", value: item });
                    }}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getTagListing(true)} />}
                    loading={!tagListing}
                    value={state.tag}
                    wrap
                  />
                </Col>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-end mt-3" style={{ gap: 10 }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={state.loading}>
                  {state.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle mr-2" />
                      Save Depreciation
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  onClick={() => dispatch({ type: "RESET_PARTIAL" })}
                  disabled={state.loading}
                >
                  <i className="fa fa-undo mr-2" />
                  Clear Amount & Note
                </button>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: Use a dedicated “Depreciation Expense” account for reporting clarity.
              </div>
            </form>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default DepreciateAssetPage;
